import {ModelExplainerConfig} from './model-explainer';

/**
 * Shared post-process contribution insight types written by the
 * PostProcessingExplainer for every model.
 */
const POST_PROCESS_COLUMNS = [
    'zip_mapping_contribution',
    'demand_curve_contribution',
    'store_hour_normalization_contribution',
    'inference_publishing_contribution',
];

const POST_PROCESS_FRIENDLY_NAMES = {
    zip_mapping_contribution: 'Zip Mapping',
    demand_curve_contribution: 'Demand Curve Application',
    store_hour_normalization_contribution: 'Store Hour Normalization',
    inference_publishing_contribution: 'Inference Publishing',
    post_process_contributions: 'Post-Process Contributions',
};

/**
 * BOSCO (Box-Cox Bayesian) explainability: SHAP-style component means written
 * by BOSCOExplainer to forecast_insight with model_name='bosco'.
 */
const BOSCO_MODEL_COMPONENT_COLUMNS = [
    'shap_global_level_mean',
    'shap_global_seasonal_mean',
    'shap_global_error_mean',
    'shap_series_level_mean',
    'shap_series_seasonal_mean',
    'shap_holiday_mean',
    'shap_series_error_mean_with_residual',
];

export const BOSCO_EXPLAINER_CONFIG: ModelExplainerConfig = {
    modelName: 'bosco',
    modelComponentColumns: BOSCO_MODEL_COMPONENT_COLUMNS,
    forecastColumns: ['raw_forecast', 'cd_mean', 'final_forecast'],
    postProcessColumns: POST_PROCESS_COLUMNS,
    finalTableColumns: [
        'cd_mean',
        'shap_global_level_mean',
        'shap_global_seasonal_mean',
        'shap_global_error_mean',
        'shap_series_level_mean',
        'shap_series_seasonal_mean',
        'shap_series_error_mean_with_residual',
        'shap_holiday_mean',
        'raw_forecast',
        'post_process_contributions',
        'final_forecast',
    ],
    friendlyNameMapping: {
        shap_global_level_mean: 'Global Trend',
        shap_global_seasonal_mean: 'Global Day-of-Week',
        shap_global_error_mean: 'Global Noise',
        shap_series_level_mean: 'Store Specific Trend',
        shap_series_seasonal_mean: 'Store Specific Day-of-Week',
        shap_holiday_mean: 'Store Specific Holiday Impact and Annual Seasonality',
        shap_series_error_mean_with_residual: 'Store Specific Noise',
        raw_forecast: 'Model Forecast',
        cd_mean: 'Training CD Average',
        final_forecast: 'Unconstrained Baseline Forecast',
        ...POST_PROCESS_FRIENDLY_NAMES,
    },
    percentageBaseColumn: 'cd_mean',
    percentageToggleLabel: 'Show % of Training CD Average',
};

/**
 * Foundation model (hierarchical Bayesian ETS) explainability: marginal-p50
 * ETS decomposition components written by FoundationExplainer to
 * forecast_insight with model_name='foundation'.
 *
 * Decomposition identity (exact):
 *   comp_level + comp_trend + comp_seasonality + comp_holiday + comp_monthly +
 *   comp_annual + comp_residual + comp_boost + quantile_gap = pred_p50
 */
const FOUNDATION_MODEL_COMPONENT_COLUMNS = [
    'comp_level',
    'comp_trend',
    'comp_seasonality',
    'comp_holiday',
    'comp_monthly',
    'comp_annual',
    'comp_residual',
    'comp_boost',
    'quantile_gap',
];

export const FOUNDATION_EXPLAINER_CONFIG: ModelExplainerConfig = {
    modelName: 'foundation',
    modelComponentColumns: FOUNDATION_MODEL_COMPONENT_COLUMNS,
    forecastColumns: ['raw_forecast', 'pred_p50', 'final_forecast'],
    postProcessColumns: POST_PROCESS_COLUMNS,
    finalTableColumns: [
        'comp_level',
        'comp_trend',
        'comp_seasonality',
        'comp_holiday',
        'comp_monthly',
        'comp_annual',
        'comp_residual',
        'comp_boost',
        'quantile_gap',
        'pred_p50',
        'raw_forecast',
        'post_process_contributions',
        'final_forecast',
    ],
    friendlyNameMapping: {
        comp_level: 'Level',
        comp_trend: 'Trend',
        comp_seasonality: 'Day-of-Week Seasonality',
        comp_holiday: 'Holiday Impact',
        comp_monthly: 'Monthly Seasonality',
        comp_annual: 'Annual Seasonality',
        comp_residual: 'Residual',
        comp_boost: 'Boost',
        quantile_gap: 'Quantile Gap',
        pred_p50: 'Model Prediction (P50)',
        raw_forecast: 'Model Forecast',
        final_forecast: 'Unconstrained Baseline Forecast',
        ...POST_PROCESS_FRIENDLY_NAMES,
    },
    percentageBaseColumn: 'pred_p50',
    percentageToggleLabel: 'Show % of Model Prediction (P50)',
};
